import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Icon,
  CssBaseline,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { CloudQueue, Memory, Videocam, GroupAdd, VerifiedUser, Business } from '@mui/icons-material';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#333333',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0e0e0',
    },
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#ffb74d',
    },
  },
});

const features = [
  { title: 'My Sync', icon: <CloudQueue />, description: 'Sync your data seamlessly across all platforms.' },
  { title: 'AI Sync', icon: <Memory />, description: 'AI-powered sync for faster and more accurate data sharing.' },
  { title: 'Hosted Sessions', icon: <Videocam />, description: 'Host sessions and manage your projects with ease.' },
  { title: 'Requested Sessions', icon: <GroupAdd />, description: 'View and manage requested sessions from users.' },
  { title: 'Verified Users', icon: <VerifiedUser />, description: 'Ensure your users are verified for security and trust.' },
  { title: 'Organizations', icon: <Business />, description: 'Create and manage organizations for team collaboration.' },
];

const borderColors = ['#1976d2', '#ffb74d', '#4caf50', '#e91e63', '#f44336', '#9c27b0'];

function ProjectVishwamitra() {
  const [hoveredCard, setHoveredCard] = useState(null);

  // Media Queries
  const isXs = useMediaQuery('(max-width:600px)');
  const isSm = useMediaQuery('(max-width:768px)');
  const isMd = useMediaQuery('(max-width:1024px)');
  const isLg = useMediaQuery('(max-width:1400px)');

  const radius = isXs ? 110 : isSm ? 180 : isMd ? 250 : 320;
  const cardSize = isXs ? 90 : isSm ? 110 : isMd ? 130 : 160;
  const iconSize = isXs ? 20 : isSm ? 28 : 35;
  const titleFontSize = isXs ? '0.6rem' : isSm ? '0.7rem' : '0.8rem';
  const descFontSize = isXs ? '0.55rem' : isSm ? '0.6rem' : '0.65rem';
  const scaleOnHover = isXs ? 1.1 : 1.4;

  const numCards = features.length;

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          position: 'relative',
          minHeight: {
            xs: '50vh',   // extra-small screens (mobile)
            sm: '70vh',   // small screens (tablets)
            md: '100vh',   // medium screens (small laptops)
            lg: '130vh',   // large screens (desktops)
            xl: '150vh',   // extra-large screens
          },
          // minHeight:'150vh',
          backgroundColor: 'black',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          px: 2,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            fontSize: isXs ? '0.7rem' : isSm ? '1.3rem' : isMd ? '1.6rem' : '2rem',
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            backgroundClip: 'text',
            color: 'transparent',
            letterSpacing: isXs ? 4 : isSm ? 5 : 6,
            // mb: 4,
          }}
        >
          Vishwamitra
        </Typography>


        {/* Cards Render */}
        {features.map((feature, index) => {
          const angle = (index / numCards) * 360;
          const x = radius * Math.cos((angle * Math.PI) / 180);
          const y = radius * Math.sin((angle * Math.PI) / 180);

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
              }}
            >
              <Card
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  borderRadius: '50%',
                  width: cardSize,
                  height: cardSize,
                  backgroundColor: '#000',
                  border: `2px solid ${borderColors[index % borderColors.length]}`,
                  boxShadow: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: `scale(${scaleOnHover})`,
                    zIndex: 10,
                    borderColor: '#ffffff',
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    textAlign: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Icon sx={{ fontSize: iconSize, color: '#1976d2' }}>
                    {feature.icon}
                  </Icon>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: titleFontSize,
                      color: '#1E90FF',
                      fontWeight: 600,
                      letterSpacing: 1,
                      mt: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  {hoveredCard === index && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: descFontSize,
                        color: '#ccc',
                        mt: 0.5,
                        letterSpacing: 1,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </ThemeProvider>
  );
}

export default ProjectVishwamitra;
